import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { login, register, sendOtp, verifyOtp } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Car, Mail, Lock, User, Phone, AlertCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const isArabic = i18n.language === 'ar';
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'passenger'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // OTP 2-step state
  const [otpStep, setOtpStep] = useState(false); // true when user needs to enter OTP
  const [otpMode, setOtpMode] = useState('login'); // 'login' | 'register'
  const [otpCode, setOtpCode] = useState('');
  const [otpPhoneMasked, setOtpPhoneMasked] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  React.useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const routeByRole = (user) => {
    if (user.role === 'admin') navigate('/admin');
    else if (user.role === 'driver') navigate('/driver');
    else navigate('/passenger');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await login(loginData);
      if (response.data?.access_token) {
        loginUser(response.data.access_token, response.data.user);
        routeByRole(response.data.user);
        return;
      }
      if (response.data?.otp_required) {
        setOtpMode('login');
        setOtpPhoneMasked(response.data.phone || '');
        setOtpStep(true);
        setResendCooldown(45);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!registerData.phone) {
      setError('Phone number required');
      return;
    }
    setLoading(true);
    try {
      const r = await sendOtp({ phone: registerData.phone, purpose: 'register' });
      setOtpMode('register');
      setOtpPhoneMasked(r.data?.phone || registerData.phone);
      setOtpStep(true);
      setResendCooldown(45);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!otpCode || otpCode.length < 4) {
      setError('Enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      if (otpMode === 'login') {
        const r = await login({ ...loginData, otp_code: otpCode });
        if (!r.data?.access_token) throw new Error('No token returned');
        loginUser(r.data.access_token, r.data.user);
        routeByRole(r.data.user);
      } else {
        const v = await verifyOtp({ phone: registerData.phone, code: otpCode, purpose: 'register' });
        const token = v.data?.otp_token;
        if (!token) throw new Error('No proof token');
        const r = await register({ ...registerData, otp_token: token });
        loginUser(r.data.access_token, r.data.user);
        routeByRole(r.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    setError('');
    try {
      if (otpMode === 'login') {
        await login(loginData); // triggers a new SMS
      } else {
        await sendOtp({ phone: registerData.phone, purpose: 'register' });
      }
      setResendCooldown(45);
    } catch (err) {
      setError(err.response?.data?.detail || 'Resend failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/saeed-logo.png" alt="Saeed" className="h-11 w-11 object-contain bg-black rounded-lg p-1" />
            <h1 className="text-2xl font-bold tracking-wide">
              SAEED
            </h1>
          </div>
          <LanguageSwitcher />
        </div>
      </div>
      
      <Card className="w-full max-w-md mt-20">
        <div className="p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">
              {isArabic ? 'مرحباً بك' : 'Welcome'}
            </h2>
            <p className="text-gray-600">
              {isArabic ? 'سجل الدخول أو أنشئ حساب جديد' : 'Sign in or create a new account'}
            </p>
          </div>
          
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {otpStep ? (
            <form onSubmit={handleSubmitOtp} className="space-y-5">
              <div className="text-center">
                <h2 className="text-xl font-bold text-black">
                  {isArabic ? 'أدخل رمز التحقق' : 'Enter verification code'}
                </h2>
                <p className="text-sm text-gray-500 mt-2">
                  {isArabic ? 'أرسلنا رمزاً من 6 أرقام إلى' : 'We sent a 6-digit code to'}<br />
                  <span className="font-bold text-black">{otpPhoneMasked}</span>
                </p>
              </div>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                className="text-3xl tracking-widest text-center h-16 font-bold"
                data-testid="otp-input"
                autoFocus
              />
              <Button type="submit" disabled={loading} className="w-full bg-black hover:bg-gray-800 h-12 text-base" data-testid="otp-submit-btn">
                {loading ? '...' : (isArabic ? 'تأكيد ومتابعة' : 'Verify & Continue')}
              </Button>
              <div className="flex flex-col items-center gap-2 pt-2">
                <button type="button" onClick={handleResend} disabled={resendCooldown > 0 || loading}
                  className={`text-sm font-bold ${resendCooldown > 0 ? 'text-gray-400' : 'text-green-600 hover:text-green-700'}`}>
                  {resendCooldown > 0
                    ? (isArabic ? `إعادة الإرسال بعد ${resendCooldown}ث` : `Resend in ${resendCooldown}s`)
                    : (isArabic ? 'إعادة إرسال الرمز' : 'Resend code')}
                </button>
                <button type="button" onClick={() => { setOtpStep(false); setOtpCode(''); setError(''); }}
                  className="text-xs text-gray-500 hover:text-gray-700">
                  {isArabic ? '← تغيير التفاصيل' : '← Change details'}
                </button>
              </div>
            </form>
          ) : (
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">
                {isArabic ? 'تسجيل الدخول' : 'Login'}
              </TabsTrigger>
              <TabsTrigger value="register">
                {isArabic ? 'التسجيل' : 'Register'}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">{isArabic ? 'البريد الإلكتروني' : 'Email'}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder={isArabic ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                      className="pl-10"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="password">{isArabic ? 'كلمة المرور' : 'Password'}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder={isArabic ? 'أدخل كلمة المرور' : 'Enter your password'}
                      className="pl-10"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-black to-black"
                  disabled={loading}
                >
                  {loading ? (isArabic ? 'جاري التحميل...' : 'Loading...') : (isArabic ? 'تسجيل الدخول' : 'Sign In')}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="name">{isArabic ? 'الاسم' : 'Name'}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="name"
                      placeholder={isArabic ? 'أدخل اسمك' : 'Enter your name'}
                      className="pl-10"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="reg-email">{isArabic ? 'البريد الإلكتروني' : 'Email'}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder={isArabic ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                      className="pl-10"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="phone">{isArabic ? 'رقم الهاتف' : 'Phone'}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="phone"
                      placeholder={isArabic ? '+963-XXX-XXXXXX' : '+963-XXX-XXXXXX'}
                      className="pl-10"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="reg-password">{isArabic ? 'كلمة المرور' : 'Password'}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="reg-password"
                      type="password"
                      placeholder={isArabic ? 'أدخل كلمة المرور' : 'Enter your password'}
                      className="pl-10"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="role">{isArabic ? 'نوع الحساب' : 'Account Type'}</Label>
                  <select
                    id="role"
                    className="w-full p-2 border rounded-md"
                    value={registerData.role}
                    onChange={(e) => setRegisterData({ ...registerData, role: e.target.value })}
                  >
                    <option value="passenger">{isArabic ? 'راكب' : 'Passenger'}</option>
                    <option value="driver">{isArabic ? 'سائق' : 'Driver'}</option>
                  </select>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-black to-black"
                  disabled={loading}
                >
                  {loading ? (isArabic ? 'جاري التحميل...' : 'Loading...') : (isArabic ? 'إنشاء حساب' : 'Create Account')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          )}
          
          <div className="mt-6 text-center">
            <Button variant="ghost" onClick={() => navigate('/')}>
              {isArabic ? 'العودة للصفحة الرئيسية' : 'Back to Home'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;